import { Link, Navigate, Outlet, type LinkProps } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import { useEffect } from 'react'
import { stickyTop } from '../../lib/sticky'
import { useI18n } from '../lib/i18n'
import { useSignOut, useStaffState } from '../lib/session'
import { endStaffSession, hadSession, rememberSessionLimits } from '../lib/session-end'
import { ErrorState, Footer, LanguageSelect, Loading, StaffBrand } from './chrome'
import { ReauthProvider } from './reauth'
import { SessionWatch } from './session-watch'

/** The panel's sections (ADR 0008 §3), as the Figma's tabs (86:2451) with «Inicio» first. */
const SECTIONS = [
  { to: '/', label: 'nav.home', exact: true },
  { to: '/empresas', label: 'nav.companies', exact: false },
  { to: '/cursos', label: 'nav.catalog', exact: false },
] as const satisfies readonly { to: LinkProps['to']; label: string; exact: boolean }[]

/*
 * The panel, for a staff session with its passkey verified (ADR 0008 §1). Eklevo's brand only,
 * never a company's. The screens of N-a.2 go inside: «Inicio», «Empresas» and «Cursos»; the
 * sign-in and passkey pages stay out. Below the desktop width, the sections take a row of their
 * own under the brand (a tablet, PRD 00 §4.8).
 */
export function ShellLayout() {
  const { t } = useI18n()
  const state = useStaffState()
  const signOut = useSignOut()
  const session = state.data?.status === 'signed-in' ? state.data : null
  // Kept for the sign-in page to say why the session ended, whichever way it ends.
  const endsAt = session ? Date.parse(session.endsAt) : null
  const idleMinutes = session?.idleMinutes ?? null
  useEffect(() => {
    if (endsAt !== null && idleMinutes !== null) rememberSessionLimits({ endsAt, idleMinutes })
  }, [endsAt, idleMinutes])
  // The session ended while the panel was open (asked again when the tab is shown, say).
  const ended = state.data?.status === 'anonymous' && hadSession()
  useEffect(() => {
    if (ended) endStaffSession()
  }, [ended])

  if (state.isPending) return <Loading />
  if (state.isError) return <ErrorState onRetry={() => void state.refetch()} />
  if (state.data.status === 'anonymous') return ended ? null : <Navigate to="/entrar" replace />
  if (state.data.status === 'second-factor') return <Navigate to="/clave" replace />
  return (
    <ReauthProvider>
      <div className="flex min-h-screen flex-col">
        <a
          href="#contenido"
          className="sr-only rounded-lg bg-surface px-3 py-2 focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50"
        >
          {t('shell.skip')}
        </a>
        <header ref={stickyTop} className="sticky top-0 z-20 border-b border-line bg-surface">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6">
            <Link to="/">
              <StaffBrand />
            </Link>
            <nav
              aria-label={t('nav.label')}
              className="order-last flex w-full gap-1 lg:order-none lg:ml-4 lg:w-auto"
            >
              {SECTIONS.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  activeOptions={{ exact: s.exact }}
                  className="inline-flex h-9 items-center rounded-lg px-3 text-sm whitespace-nowrap text-ink-muted hover:text-ink aria-[current=page]:bg-page aria-[current=page]:text-ink"
                >
                  {t(s.label)}
                </Link>
              ))}
            </nav>
            <div className="ml-auto flex items-center gap-2">
              <LanguageSelect />
              <button
                type="button"
                onClick={() => signOut.mutate()}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm text-ink-muted hover:bg-black/5 hover:text-ink"
              >
                <LogOut aria-hidden className="size-4" />
                <span className="hidden sm:inline">{t('common.signOut')}</span>
                <span className="sr-only sm:hidden">{t('common.signOut')}</span>
              </button>
            </div>
          </div>
        </header>
        <SessionWatch session={state.data} />
        <main
          id="contenido"
          tabIndex={-1}
          className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 outline-none sm:px-6"
        >
          <Outlet />
        </main>
        <Footer />
      </div>
    </ReauthProvider>
  )
}
